import { useAutoAnimate } from "@formkit/auto-animate/react";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import {
  RiAddLine,
  RiArrowDownLine,
  RiDeleteBin6Line,
  RiRefreshLine,
} from "react-icons/ri";
import { memoryRequest } from "api/assistantMemory";
import { subscribeMemoryChanges } from "api/subscribeMemoryChanges";
import MemorySubmissions from "./memory/MemorySubmissions";
import MemoryAlert from "./memory/MemoryAlert";
import MemoryButton from "./memory/MemoryButton";
import MemoryCard from "./memory/MemoryCard";
import MemoryEditor from "./memory/MemoryEditor";
import MemoryEmpty from "./memory/MemoryEmpty";
import MemoryEvidence from "./memory/MemoryEvidence";
import MemoryHeader from "./memory/MemoryHeader";
import MemorySearch from "./memory/MemorySearch";
import MemorySettings from "./memory/MemorySettings";
import MemorySkeleton from "./memory/MemorySkeleton";
import css from "./memory/memory.module.scss";

const isReview = (fact) => fact.qualityStatus === "needs_review";
const SEARCH_DELAY_MS = 300;

const memoriesPath = ({ query, sort }, cursor) => {
  const params = new URLSearchParams();
  if (query) params.set("q", query);
  if (sort !== "newest") params.set("sort", sort);
  if (cursor) params.set("cursor", cursor);
  const search = params.toString();
  return "/memories" + (search ? "?" + search : "");
};

export default function MemoryPanel({ onBack }) {
  const [stackParent] = useAutoAnimate({ duration: 200 });
  const [memoriesParent] = useAutoAnimate({ duration: 200 });
  const [reviewParent] = useAutoAnimate({ duration: 200 });
  const [submissionVersion, setSubmissionVersion] = useState(0);
  const [uncertainSave, setUncertainSave] = useState(null);
  const [evidenceGeneration, setEvidenceGeneration] = useState(0);
  const [memories, setMemories] = useState([]);
  const [state, setState] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("newest");
  // True while a new search or sort is loading, so old results never show
  // under the new sort.
  const [reloading, setReloading] = useState(false);
  // A failed search or sort drops the old results and their cursor, so Load
  // more can never mix pages from two different queries.
  const [listFailed, setListFailed] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("load");
  const [editing, setEditing] = useState(null);
  const [text, setText] = useState("");
  const [clearConfirm, setClearConfirm] = useState(false);
  // { fact, alsoHidden } while the user decides on a forget that also hides
  // other facts from the same message.
  const [forgetConfirm, setForgetConfirm] = useState(null);
  const [notice, setNotice] = useState("");
  const lock = useRef(false);
  const accountGeneration = useRef(0);
  const mounted = useRef(true);
  const requestId = useRef(null);
  const loadGeneration = useRef(0);
  // Load more, Retry and background refreshes read the current search from
  // here, so they never fall back to an unfiltered list.
  const view = useRef({ query: "", sort: "newest" });
  const load = useCallback(async (nextCursor = null, signal) => {
    const generation = ++loadGeneration.current;
    const data = await memoryRequest(memoriesPath(view.current, nextCursor), {
      signal,
    });
    if (!mounted.current || generation !== loadGeneration.current) return;
    setMemories((current) =>
      nextCursor ? [...current, ...data.memories] : data.memories,
    );
    setState(data.state);
    setCursor(data.nextCursor);
    setTotal(data.total ?? 0);
    setListFailed(false);
  }, []);
  const handleSubmissionsCompleted = useCallback(async () => {
    await load();
    if (!mounted.current) return;
    setNotice((current) =>
      [
        "Saved. Processing for assistant use.",
        "Your text is saved. Check its processing status below.",
      ].includes(current)
        ? ""
        : current,
    );
  }, [load]);
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    mounted.current = true;
    view.current = { query, sort };
    const controller = new AbortController();
    setError("");
    setReloading(true);
    load(null, controller.signal)
      .catch((e) => {
        if (controller.signal.aborted) return;
        setMemories([]);
        setCursor(null);
        setTotal(0);
        setListFailed(true);
        setError(e.message);
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setReloading(false);
        setBusy((current) => (current === "load" ? "" : current));
      });
    return () => {
      mounted.current = false;
      controller.abort();
    };
  }, [load, query, sort]);

  useEffect(() => {
    let timer;
    const controller = new AbortController();
    const unsubscribe = subscribeMemoryChanges((change) => {
      setEvidenceGeneration((value) => value + 1);
      ++loadGeneration.current;
      if (change?.accountChanged) {
        accountGeneration.current++;
        setMemories([]);
        setState(null);
        setUncertainSave(null);
        setText("");
        requestId.current = null;
        setEditing(null);
        setCursor(null);
      }
      clearTimeout(timer);
      timer = setTimeout(() => {
        load(null, controller.signal).catch((e) => {
          if (!controller.signal.aborted) setError(e.message);
        });
      }, 300);
    });
    return () => {
      clearTimeout(timer);
      controller.abort();
      unsubscribe();
    };
  }, [load]);

  const run = async (key, action) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(key);
    setError("");
    setNotice("");
    try {
      await action();
    } catch (e) {
      if (mounted.current) setError(e.message);
    } finally {
      lock.current = false;
      if (mounted.current) setBusy("");
    }
  };
  const changeSetting = (key, value) =>
    run(key, async () => {
      const result = await memoryRequest("/memory-settings", {
        method: "PATCH",
        body: { [key]: value },
      });
      setState(result.state);
    });
  const save = () =>
    run("save", async () => {
      const account = accountGeneration.current;
      requestId.current ||= crypto.randomUUID();
      const payload = uncertainSave || {
        text,
        requestId: requestId.current,
        ...(editing === "new"
          ? {}
          : { targetId: editing.id, revision: editing.revision }),
      };
      try {
        await memoryRequest("/memory-submissions", {
          method: "POST",
          body: payload,
        });
      } catch (failure) {
        if (account !== accountGeneration.current || !mounted.current) return;
        if (!failure.status || failure.status >= 500) {
          setUncertainSave(payload);
          setNotice(
            "Checking save status. Your text is preserved. Retry checks the same request.",
          );
          return;
        }
        setUncertainSave(null);
        requestId.current = null;
        setNotice("");
        throw failure;
      }
      if (account !== accountGeneration.current || !mounted.current) return;
      setEditing(null);
      setText("");
      setUncertainSave(null);
      requestId.current = null;
      setSubmissionVersion((value) => value + 1);
      setNotice("Saved. Processing for assistant use.");
      try {
        await load();
      } catch {
        setError(
          "Your text was saved, but the list could not refresh. Refresh the list to see it.",
        );
      }
    });
  useEffect(() => {
    if (!uncertainSave) return;
    const controller = new AbortController();
    let checking = false;
    const check = async () => {
      if (checking) return;
      checking = true;
      try {
        await memoryRequest(
          "/memory-submissions/requests/" +
            encodeURIComponent(uncertainSave.requestId),
          { signal: controller.signal },
        );
        if (controller.signal.aborted) return;
        setUncertainSave(null);
        setEditing(null);
        setText("");
        requestId.current = null;
        setSubmissionVersion((value) => value + 1);
        setNotice("Your text is saved. Check its processing status below.");
        load().catch(() => {});
      } catch (failure) {
        if (!controller.signal.aborted && failure.status === 403) {
          setUncertainSave(null);
          requestId.current = null;
          setNotice("");
          setError(failure.message);
        }
        /* A missing or unavailable response does not prove the save failed. */
      } finally {
        checking = false;
      }
    };
    void check();
    const timer = setInterval(check, 3000);
    return () => {
      controller.abort();
      clearInterval(timer);
    };
  }, [uncertainSave, load]);
  // Forget blocks the whole message a fact came from, so other facts from that
  // message stop being used too. Ask first when that would happen.
  const forget = (fact, confirmed = null) =>
    run(fact.id, async () => {
      if (!confirmed) {
        const { alsoHidden = [] } = await memoryRequest(
          "/memories/" + fact.id + "/forget-impact",
        );
        if (alsoHidden.length) {
          setForgetConfirm({ fact, alsoHidden });
          return;
        }
      }
      await memoryRequest("/memories/" + fact.id, {
        method: "DELETE",
        body: { revision: fact.revision },
      });
      const hiddenIds = new Set((confirmed || []).map((item) => item.id));
      setForgetConfirm(null);
      setMemories((current) =>
        current
          .filter((item) => item.slot !== fact.slot)
          .map((item) =>
            hiddenIds.has(item.id) ? { ...item, hidden: true } : item,
          ),
      );
      setTotal((current) => Math.max(0, current - 1));
      setNotice(
        hiddenIds.size
          ? "Memory forgotten. Facts from the same message are now hidden."
          : "Memory forgotten.",
      );
    });
  const startEditing = (target) => {
    if (uncertainSave) return;
    setEditing(target);
    setText(target === "new" ? "" : target.text);
    requestId.current = null;
  };

  const loading = busy === "load" && (!state || listFailed);
  const listLoading = loading || reloading;
  const shown = listLoading ? [] : memories;
  const clear = shown.filter((fact) => !isReview(fact));
  const review = shown.filter(isReview);
  // A correction whose card is not in the list (a background refresh reloaded
  // page one, or a search is loading) stays open at the top, so unsaved text
  // is never lost.
  const editingInList =
    editing !== "new" && shown.some((fact) => fact.id === editing?.id);
  const editor = editing && (
    <MemoryEditor
      key={editing === "new" ? "new" : editing.id}
      isNew={editing === "new"}
      text={text}
      busy={uncertainSave ? "checking" : busy}
      onChange={(value) => {
        setText(value);
        requestId.current = null;
      }}
      onSave={save}
      onCancel={() => setEditing(null)}
    />
  );
  // A correction opens in the card's own place in the list; only a new fact
  // opens at the top, under the toolbar.
  const renderCard = (fact, index) =>
    editing?.id === fact.id ? (
      editor
    ) : (
      <Fragment key={fact.id}>
        <MemoryCard
          fact={fact}
          index={index}
          busy={busy}
          onEdit={() => startEditing(fact)}
          onForget={() => forget(fact)}
        >
          {busy !== fact.id && busy !== "clear" && (
            <MemoryEvidence
              key={`${fact.id}:${fact.revision}:${evidenceGeneration}`}
              fact={fact}
            />
          )}
        </MemoryCard>
        {forgetConfirm?.fact.id === fact.id && (
          <MemoryAlert tone="danger">
            <span>
              This fact came from a message that also saved the facts below.
              Forgetting it hides them from the assistant too:
            </span>
            <ul className={css.alertList}>
              {forgetConfirm.alsoHidden.map((item) => (
                <li key={item.id}>{item.text}</li>
              ))}
            </ul>
            <span className={css.alertRow}>
              <MemoryButton
                variant="dangerSolid"
                loading={busy === fact.id}
                disabled={Boolean(busy) && busy !== fact.id}
                onClick={() => forget(fact, forgetConfirm.alsoHidden)}
              >
                Forget anyway
              </MemoryButton>
              <MemoryButton
                variant="ghost"
                disabled={Boolean(busy)}
                onClick={() => setForgetConfirm(null)}
              >
                Keep memory
              </MemoryButton>
            </span>
          </MemoryAlert>
        )}
      </Fragment>
    );

  return (
    <section
      className={css.panel}
      aria-label="Saved memories"
      aria-busy={Boolean(busy)}
    >
      <MemoryHeader
        onBack={onBack}
        count={total}
        query={query}
        loading={loading}
      />

      <div className={css.scroll}>
        <div ref={stackParent} className={css.stack}>
          {error && (
            <MemoryAlert tone="danger" role="alert">
              <span>{error}</span>
              {(!state || listFailed) && (
                <span className={css.alertRow}>
                  <MemoryButton
                    variant="ghost"
                    icon={<RiRefreshLine />}
                    disabled={Boolean(busy)}
                    onClick={() => run("load", () => load())}
                  >
                    Retry
                  </MemoryButton>
                </span>
              )}
            </MemoryAlert>
          )}

          <div className={css.live} role="status" aria-live="polite">
            {notice || (busy === "load" ? "Loading memories…" : "")}
          </div>

          {state && (
            <MemorySettings
              state={state}
              busy={busy}
              onChange={changeSetting}
            />
          )}

          {state?.clearing && (
            <MemoryAlert>
              <span>Memories are hidden while cleanup finishes.</span>
              <span className={css.alertRow}>
                <MemoryButton
                  variant="ghost"
                  icon={<RiRefreshLine />}
                  onClick={() => run("load", () => load())}
                >
                  Refresh
                </MemoryButton>
              </span>
            </MemoryAlert>
          )}

          {state && !state.clearing && (
            <div className={css.toolbar}>
              <MemoryButton
                variant="primary"
                size="md"
                trailing={<RiAddLine />}
                disabled={Boolean(busy)}
                onClick={() => startEditing("new")}
              >
                Remember a fact
              </MemoryButton>
              <MemoryButton
                variant="danger"
                icon={<RiDeleteBin6Line />}
                disabled={Boolean(busy)}
                onClick={() => setClearConfirm(true)}
              >
                Clear all
              </MemoryButton>
            </div>
          )}

          {clearConfirm && (
            <MemoryAlert tone="danger">
              <span>
                Forget all saved facts? Old processing jobs and historical
                imports will not restore them.
              </span>
              <span className={css.alertRow}>
                <MemoryButton
                  variant="dangerSolid"
                  loading={busy === "clear"}
                  disabled={Boolean(busy) && busy !== "clear"}
                  onClick={() =>
                    run("clear", async () => {
                      const result = await memoryRequest("/memories", {
                        method: "DELETE",
                        body: { clearAll: true },
                      });
                      setState(result.state);
                      setMemories([]);
                      setCursor(null);
                      setTotal(0);
                      setClearConfirm(false);
                      setNotice(
                        "All memories are hidden. Cleanup is in progress.",
                      );
                    })
                  }
                >
                  Forget all memories
                </MemoryButton>
                <MemoryButton
                  variant="ghost"
                  disabled={Boolean(busy)}
                  onClick={() => setClearConfirm(false)}
                >
                  Keep memories
                </MemoryButton>
              </span>
            </MemoryAlert>
          )}

          {uncertainSave && (
            <MemoryAlert>
              <span>
                The save outcome is not confirmed yet. Retry uses the same
                request and cannot create a second submission.
              </span>
              <MemoryButton loading={busy === "save"} onClick={save}>
                Check or retry save
              </MemoryButton>
            </MemoryAlert>
          )}
          {!editingInList && editor}
          {state && !state?.clearing && (
            <MemorySubmissions
              onCompleted={handleSubmissionsCompleted}
              refreshKey={submissionVersion}
              recallEnabled={state?.recallEnabled}
            />
          )}

          {state && !state.clearing && (
            <MemorySearch
              search={search}
              sort={sort}
              onSearch={setSearch}
              onSort={setSort}
            />
          )}

          {listLoading && <MemorySkeleton cardsOnly={Boolean(state)} />}

          {!listLoading &&
            !memories.length &&
            state &&
            !state.clearing &&
            !listFailed &&
            !busy && (
              <MemoryEmpty query={query} onClearSearch={() => setSearch("")} />
            )}

          <div ref={memoriesParent} className={css.group}>
            {clear.map(renderCard)}
          </div>

          <div ref={reviewParent} className={css.group}>
            {!!review.length && (
              <div className={css.groupHead}>
                <h3 className={css.groupTitle}>Needs review</h3>
                <span className={css.groupHint}>
                  These memories need clarification and are not used in
                  assistant answers.
                </span>
              </div>
            )}
            {review.map(renderCard)}
          </div>

          {cursor && !listLoading && (
            <MemoryButton
              variant="ghost"
              className={css.loadMore}
              icon={<RiArrowDownLine />}
              loading={busy === "more"}
              disabled={Boolean(busy) && busy !== "more"}
              onClick={() => run("more", () => load(cursor))}
            >
              Load more
            </MemoryButton>
          )}
        </div>
      </div>
    </section>
  );
}
